import React, { useEffect, useState } from 'react';
import { FaMedal, FaBook, FaFileAlt, FaClock, FaChartLine } from 'react-icons/fa';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import { getCurrentBookInfo, getBooksVisualization, formatToK } from '../config/booksConfig';

export default function StatisticsMobile({ statistics = null, period = 'global', onPeriodChange, loading = false }) {
  const [animatedValues, setAnimatedValues] = useState({});

  const defaultStats = {
    percentile: 0,
    charactersGenerated: 0,
    reportsGenerated: 0,
    averageReportsPerDay: 0
  };

  const stats = statistics || defaultStats;
  const currentBook = getCurrentBookInfo(stats.charactersGenerated || 0);
  const booksVisualization = getBooksVisualization(stats.charactersGenerated || 0);

  useEffect(() => {
    if (!loading) {
      const targets = {
        percentile: stats.percentile || 0,
        characters: stats.charactersGenerated || 0,
        reports: stats.reportsGenerated || 0,
        avgReports: (stats.averageReportsPerDay || 0),
        timeProgress: (stats.timeProgress || 0)
      };

      Object.keys(targets).forEach(key => {
        const end = targets[key];
        const duration = 1000;
        const startTime = Date.now();
        const animate = () => {
          const progress = Math.min((Date.now() - startTime) / duration, 1);
          const current = Math.floor(progress * end);
          setAnimatedValues(prev => ({ ...prev, [key]: current }));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      });
    }
  }, [loading, stats]);

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '14px',
      padding: '14px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      gap: '10px'
    },
    periodToggle: {
      display: 'flex',
      background: '#f3f4f6',
      borderRadius: '10px',
      padding: '2px',
      gap: '2px'
    },
    periodBtn: {
      padding: '6px 10px',
      border: 'none',
      background: 'transparent',
      color: '#6b7280',
      fontWeight: '600',
      cursor: 'pointer',
      borderRadius: '7px',
      fontSize: '12px'
    },
    periodBtnActive: {
      background: 'white',
      color: '#e74c3c',
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '10px'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      padding: '12px',
      boxShadow: '0 1px 8px rgba(0, 0, 0, 0.04)',
      border: '1px solid #f0f0f0'
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '8px'
    },
    label: {
      fontSize: '12px',
      fontWeight: '700',
      color: '#6b7280',
      textTransform: 'uppercase'
    },
    iconWrapper: {
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px'
    },
    mainValue: {
      display: 'flex',
      alignItems: 'baseline',
      gap: '4px',
      marginBottom: '6px'
    },
    number: {
      fontSize: '24px',
      fontWeight: '800',
      color: '#1f2937'
    },
    unit: {
      fontSize: '12px',
      color: '#6b7280',
      fontWeight: '600'
    },
    subtitle: {
      fontSize: '11px',
      color: '#9ca3af'
    },
    avgPill: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      background: 'linear-gradient(135deg, #10b981, #059669)',
      color: 'white',
      padding: '8px 12px',
      borderRadius: '10px',
      width: '100%'
    },
    avgLabel: {
      fontSize: '10px',
      fontWeight: '700',
      letterSpacing: '0.3px'
    },
    avgValueRow: {
      display: 'flex',
      alignItems: 'baseline',
      gap: '6px'
    },
    avgValue: {
      fontSize: '20px',
      fontWeight: '800'
    },
    avgSuffix: {
      fontSize: '12px',
      fontWeight: '600'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <SectionHeader icon={FaChartLine} title="Vos statistiques" variant="warning" titleGradient={true} />
      </div>

      <div style={styles.grid}>
        {/* Rapports */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>Rapports</div>
            <div style={{ ...styles.iconWrapper, background: 'linear-gradient(135deg, #10b981, #059669)' }}>
              <FaFileAlt style={{ color: 'white' }} />
            </div>
          </div>
          <div style={styles.mainValue}>
            <span style={styles.number}>{(animatedValues.reports || 0).toLocaleString()}</span>
            <span style={styles.unit}>rapports</span>
          </div>
          <div style={styles.avgPill}>
            <div style={styles.avgLabel}>Moyenne</div>
            <div style={styles.avgValueRow}>
              <span style={styles.avgValue}>{animatedValues.avgReports || 0}</span>
              <span style={styles.avgSuffix}>/ jour</span>
            </div>
          </div>
        </div>

        {/* Caractères */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>Caractères</div>
            <div style={{ ...styles.iconWrapper, background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
              <FaBook style={{ color: 'white' }} />
            </div>
          </div>
          <div style={styles.mainValue}>
            <span style={styles.number}>{formatToK(stats.charactersGenerated || 0)}</span>
            <span style={styles.unit}>caractères</span>
          </div>
          <div style={{ display: 'flex', gap: '4px', marginTop: 8 }}>
            {booksVisualization.slice(0, 12).map((book, index) => (
              <div key={index} style={{ width: 12, height: 16, background: '#f3f4f6', borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
                {(book.filled || book.partial) && (
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${book.fillPercent}%`, background: book.color }} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Classement */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>Classement</div>
            <div style={{ ...styles.iconWrapper, background: 'linear-gradient(135deg, #fbbf24, #f59e0b)' }}>
              <FaMedal style={{ color: 'white' }} />
            </div>
          </div>
          <div style={styles.mainValue}>
            <span style={styles.number}>{animatedValues.percentile || 0}</span>
            <span style={styles.unit}>ᵉ percentile</span>
          </div>
        </div>

        {/* Temps économisé */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>Temps économisé</div>
            <div style={{ ...styles.iconWrapper, background: 'linear-gradient(135deg, #f43f5e, #dc2626)' }}>
              <FaClock style={{ color: 'white' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
            {stats.months > 0 && (
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{stats.months}</div>
                <div style={{ fontSize: 10, color: '#6b7280' }}>mois</div>
              </div>
            )}
            {(stats.months > 0 || stats.days > 0) && (
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{stats.remainingDays}</div>
                <div style={{ fontSize: 10, color: '#6b7280' }}>jours</div>
              </div>
            )}
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{stats.remainingHours || 0}</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>heures</div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>~{(stats.totalHours || 0).toLocaleString()} h totales</div>
        </div>
      </div>
    </div>
  );
}


